import {  Component, Input, Output, EventEmitter, ChangeDetectorRef, ElementRef, AfterViewInit, NgZone  } from '@angular/core';
import { JobService } from '@intranet/modules/jobs/job.service';
import { LinkedJob } from '@intranet/modules/jobs/job.types';

@Component({
    selector: 'margin-bar',
    templateUrl: './margin-bar.component.html',
    styleUrls: ['./margin-bar.component.scss']
})
export class MarginBarComponent {
    @Input() value: number = 0;
    @Input() margin: number = 0;
    @Input() marginYTD: number = 0;
    @Input() isLinked = false;
    @Input() jobId!: number;
    @Output() select = new EventEmitter<void>();
    private linkedJobs: LinkedJob[] = [];
    private observer?: IntersectionObserver;
    private inFlight = false;
    ready = false;

    private hasLoaded = false;

    linkedTooltip = 'This job is linked';

    constructor(
        private jobService: JobService,
        private cd: ChangeDetectorRef,
        private el: ElementRef<HTMLElement>,
        private zone: NgZone
    ) {}

    ngAfterViewInit(): void {
        if (!this.isLinked || this.hasLoaded) return;

        // Defer tooltip/observer init to after first paint to avoid flicker
        requestAnimationFrame(() => {
            this.ready = true;
            this.cd.detectChanges(); // commit 'ready' once, post-paint

            this.zone.runOutsideAngular(() => {
                this.observer = new IntersectionObserver((entries) => {
                    const visible = entries.some(e => e.isIntersecting);
                    if (visible) {
                        this.observer?.disconnect();
                        this.zone.run(() => this.prefetchLinked());
                    }
                }, { root: null, threshold: 0.01 });
                this.observer.observe(this.el.nativeElement);
            });
        });
    }


    ngOnDestroy() { this.observer?.disconnect(); }

    private prefetchLinked(): void {
        if (!this.isLinked || !this.jobId || this.hasLoaded || this.inFlight) return;

        this.inFlight = true;

        this.jobService.getLinkedJobDetailsCached(this.jobId).subscribe({
            next: (res) => {
                this.linkedJobs = res || [];
                this.hasLoaded = true;
                this.inFlight = false;


                this.linkedTooltip = this.linkedJobs
                    .map(j => `${j.jobNumber} – ${j.jobTitle}${j.isParent ? ' (Parent)' : ''}`)
                    .join('\n') || 'No linked jobs';
                this.cd.detectChanges();
            },
            error: () => {
                this.inFlight = false;
                this.linkedTooltip = 'Error loading linked jobs';
                this.cd.detectChanges();
            }
        });
    }

    // radius of our SVG circle
    readonly radius = 18;
    // circumference = 2πr
    readonly circumference = 2 * Math.PI * this.radius;

    onClick() {
      this.select.emit();
    }

    get ytdPercentage(): number {
        return this.marginYTD > 0 ? Math.round((this.value / this.marginYTD) * 100) : 0;
    }

    get totalPercentage(): number {
        return this.margin > 0 ? Math.round((this.value / this.margin) * 100) : 0;
    }

    get ytdWidth(): number { return Math.min(this.ytdPercentage, 100); }
    get totalWidth(): number { return Math.min(this.totalPercentage, 100); }

    getBackgroundColor(percent: number): string {
        if (percent <= 100) return '#2bae2b';
        if (percent <= 110) return '#eba121';
        return '#d21212';
    }
    get totalGradient(): string { return this.getBackgroundColor(this.totalPercentage); }
    get ytdGradient(): string   { return this.getBackgroundColor(this.ytdPercentage); }


    // Given a fill-percentage (0–100), compute the SVG stroke-dashoffset
    getDashOffset(percent: number): number {
        const fraction = percent / 100;
        return this.circumference * (1 - fraction);
    }

    onHover(): void {
        if (!this.ready || !this.isLinked) return;

        // Already loaded: ensure tooltip shows final text
        if (this.hasLoaded) {
            this.linkedTooltip = this.linkedJobs
                .map(j => `${j.jobNumber} – ${j.jobTitle}${j.isParent ? ' (Parent)' : ''}`)
                .join('\n') || 'No linked jobs';
            this.cd.detectChanges();
            return;
        }

        // Not loaded yet: show a placeholder and kick off fetch (once)
        this.linkedTooltip = 'Loading...';
        this.cd.detectChanges();

        if (this.inFlight) return;
        this.prefetchLinked();
    }
}